import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, convertToParamMap } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { combineLatest, of } from 'rxjs';

// Asegúrate de que la ruta al servicio sea correcta según tu estructura
import { ChromadbService } from '../../services/chromadb.service';  

export interface TopicItem {
  topic: string;
  count: number;
}

@Component({
  selector: 'app-topics',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './topics.component.html',
  styles: [] // No necesitamos CSS externo gracias a Tailwind
})

export class TopicsComponent implements OnInit {
  
  topics: TopicItem[] = [];
  isLoading: boolean = true;
  error: string | null = null;
  maxCount: number = 0; // Usado para calcular el ancho relativo (%) de las barras
  collectionId: string | null = null;
  collectionsFilter: string | null = null;
  startDate: string = '';
  endDate: string = '';

  constructor(
    private chromaService: ChromadbService,
    private route: ActivatedRoute
  ) {
    // Inicializar fechas por defecto (hace 3 meses a hoy)
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 3);
    
    this.endDate = end.toISOString().split('T')[0];
    this.startDate = start.toISOString().split('T')[0];
  }

  ngOnInit(): void {
    const paramMap$ = this.route.paramMap || of(convertToParamMap({}));
    const queryParamMap$ = this.route.queryParamMap || of(convertToParamMap({}));
    combineLatest([paramMap$, queryParamMap$]).subscribe(([paramMap, queryMap]) => {
      this.collectionId = paramMap.get('collection');
      this.collectionsFilter = queryMap.get('collections');
      const qStart = queryMap.get('startDate') || queryMap.get('start_date');
      if (qStart) {
        this.startDate = qStart;
      }
      const qEnd = queryMap.get('endDate') || queryMap.get('end_date');
      if (qEnd) {
        this.endDate = qEnd;
      }
      this.fetchTopics();
    });
  }

  getFilteredCollectionsCount(): number {
    if (!this.collectionsFilter) return 0;
    return this.collectionsFilter.split(',').filter(c => c.trim().length > 0).length;
  }

  fetchTopics(): void {
    this.isLoading = true;
    
    // Si tenemos un collectionId o un filtro de colecciones, lo pasamos al servicio
    this.chromaService.getTopics(
      this.collectionId || undefined, 
      this.startDate, 
      this.endDate, 
      this.collectionsFilter || undefined
    ).subscribe({
      next: (response: any) => {
        // Asumimos que la respuesta viene en response.data según el endpoint Flask
        this.topics = response.data || [];
        console.log('Topics received:', this.topics.length)
        
        // Calculamos el valor máximo para que la barra más larga sea el 100%
        if (this.topics.length > 0) {
          this.maxCount = Math.max(...this.topics.map(t => t.count));
        }
        
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching topics:', err);
        this.error = 'Unable to load topics. Please try again later.';
        this.isLoading = false;
      }
    });
  }

  /**
   * Calcula el ancho en porcentaje para la barra de frecuencia.
   * La palabra más repetida tendrá un ancho del 100%.
   */
  getBarWidth(count: number): string {
    if (this.maxCount === 0) return '0%';
    const percentage = (count / this.maxCount) * 100;
    return `${percentage}%`;
  }

downloadCSV(): void {
    if (!this.topics || this.topics.length === 0) {
      return;
    }

    // 1. Definir encabezados
    const headers = ['Topic', 'Frequency'];
    
    // 2. Convertir datos a formato CSV (Manejando comas dentro de los textos)
    const csvRows = this.topics.map(item => {
      // Escapamos las comillas dobles si existen y envolvemos el texto en comillas
      const safeTopic = `"${item.topic.replace(/"/g, '""')}"`;
      return `${safeTopic},${item.count}`;
    });

    // 3. Unir todo con saltos de línea
    const csvContent = [headers.join(','), ...csvRows].join('\n');

    // 4. Crear el Blob
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // 5. Crear enlace temporal para descarga
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    
    // Nombre del archivo con fecha
    const date = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `topics_analysis_${date}.csv`);
    
    // Simular clic y limpiar
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}